import { useMemo } from 'react';
import { useQuery, keepPreviousData } from '@tanstack/react-query';

import { getCollectionHexagons } from '@utils/api';
import { collectionHexagonsToCells } from '../utils/collectionHexagonsToCells';

/**
 * Fetches and shapes the collection's hexagon aggregation for the current
 * project.
 *
 * Data flow:
 *   persisted filters + visible objects → buildHexagonFilterBody (in the
 *   consumer) → this hook → getCollectionHexagons (GeoJSON) →
 *   collectionHexagonsToCells → cells ready for the map layer / info panel.
 *
 * Refetch triggers (all part of the query key or handled by invalidation):
 *   - `resolution` change (different lattice)
 *   - `filterBody` change — this only changes when filters are *applied*
 *     (persisted), so dragging a slider does not refetch, and when object
 *     visibility toggles (visible keys are the aggregation selector)
 *   - a finished detection job — `collectionHexagons` is in
 *     COLLECTION_RESULT_QUERY_KEYS, so invalidateCollectionResults refreshes it
 *
 * The request is gated on there being at least one visible object: an empty
 * `filterBody` would otherwise be read by the endpoint as "aggregate every
 * object", which is the opposite of "nothing is visible".
 *
 * @param {string} projectUuid    - current project UUID
 * @param {string} collectionUuid - selected collection UUID
 * @param {number} resolution     - hexagon area in m²
 * @param {object} filterBody     - per-object range map from buildHexagonFilterBody
 * @returns {{ cells: import('../utils/collectionHexagonsToCells').HexagonCell[],
 *   isLoading: boolean, isFetching: boolean, error: unknown }}
 */
const useCollectionHexagons = (projectUuid, collectionUuid, resolution, filterBody) => {
	// At least one visible object → something to aggregate. Also guards the
	// "empty body means aggregate-all" caveat documented in buildHexagonFilterBody.
	const hasVisibleObjects = Object.keys(filterBody ?? {}).length > 0;

	const { data, isLoading, isFetching, error } = useQuery({
		// React Query hashes the key deterministically, so the filterBody object
		// is a stable dependency — identical filters won't trigger a refetch.
		queryKey: ['collectionHexagons', projectUuid, collectionUuid, resolution, filterBody],
		queryFn: () => getCollectionHexagons(projectUuid, collectionUuid, resolution, filterBody),
		enabled: !!projectUuid && !!collectionUuid && hasVisibleObjects,
		// Keep the previous cells on screen while a new resolution/filter/visibility
		// aggregation loads — avoids the layer flickering empty on every toggle.
		// `isFetching` drives the loading affordance in the meantime.
		placeholderData: keepPreviousData,
		// Stable until a key input changes or a detection job invalidates it.
		staleTime: 10 * 60 * 1000,
		// Empty states (204/404) resolve to [] rather than throwing, so retrying a
		// real error wouldn't help here.
		retry: false,
	});

	const cells = useMemo(
		// When nothing is visible the query is disabled; keepPreviousData would
		// otherwise retain the last cells, so clear them explicitly.
		() => (hasVisibleObjects ? collectionHexagonsToCells(data ?? [], resolution) : []),
		[hasVisibleObjects, data, resolution]
	);

	return { cells, isLoading, isFetching, error };
};

export default useCollectionHexagons;
